import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { PoreFrac } from '../../models/poreFrac.model';
import { UnitsGridColumn, UnitsGridPasteTransform } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { valuesIncreaseValidator } from 'src/app/shared/components/ng-table-grid/shared-grid.validators';
import { greaterThanZero } from 'src/app/wellbore-inputs/components/well-configuration/validation/greaterThanZero';
import { greaterThanOrEqualToZero } from 'src/app/wellbore-inputs/components/well-configuration/validation/greaterThanOrEqualToZero';

export function createPoreFracColumns(userUnits: UserUnitsModel): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'trueVerticalDepth',
      header: 'TVD',
      unitKey: 'longLengths',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'equivalentMudWeight',
      header: 'EMW',
      unitSymbol: unitsLib[userUnits.density].symbol,
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'pressure',
      header: 'Pressure',
      unitKey: 'pressure',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 1
    }
  ];
}

export function createPoreFracRow(data?: Partial<PoreFrac>): FormGroup {
  return new FormGroup({
    trueVerticalDepth: new FormControl<number | null>(data?.trueVerticalDepth ?? 0, [Validators.required, valuesIncreaseValidator('trueVerticalDepth')]),
    equivalentMudWeight: new FormControl<number | null>(data?.equivalentMudWeight ?? null, [greaterThanZero]),
    pressure: new FormControl<number | null>(data?.pressure ?? null, [greaterThanOrEqualToZero])
  });
}

export function isPoreFracRowEmpty(row: FormGroup): boolean {
  const trueVerticalDepth = row.get('trueVerticalDepth')?.value;
  const equivalentMudWeight = row.get('equivalentMudWeight')?.value;
  const pressure = row.get('pressure')?.value;
  const isTvdEmpty = trueVerticalDepth === null || trueVerticalDepth === undefined || trueVerticalDepth === '' || Number.isNaN(Number(trueVerticalDepth)) || Number(trueVerticalDepth) === 0;
  const isEmwEmpty = equivalentMudWeight === null || equivalentMudWeight === undefined || equivalentMudWeight === '' || Number.isNaN(Number(equivalentMudWeight));
  const isPressureEmpty = pressure === null || pressure === undefined || pressure === '' || Number.isNaN(Number(pressure));
  return isTvdEmpty && isEmwEmpty && isPressureEmpty;
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>, createRow: () => FormGroup): void {
  if (formArray.length === 0) {
    formArray.push(createRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isPoreFracRowEmpty(formArray.at(formArray.length - 1)) &&
    isPoreFracRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  if (!isPoreFracRowEmpty(formArray.at(formArray.length - 1))) {
    formArray.push(createRow());
  }
}

export function populatePoreFracFormArray(
  formArray: FormArray<FormGroup>,
  points: readonly PoreFrac[],
  createRow: (data?: Partial<PoreFrac>) => FormGroup
): void {
  formArray.clear();
  points.forEach((point) => formArray.push(createRow(point)));
  ensureTrailingInputRow(formArray, () => createRow());
}

export function extractPoreFracRows(formArray: FormArray<FormGroup>): PoreFrac[] {
  return formArray.controls
    .filter((control) => !isPoreFracRowEmpty(control as FormGroup))
    .map((control) => ({
      trueVerticalDepth: Number(control.get('trueVerticalDepth')?.value ?? 0),
      equivalentMudWeight: Number(control.get('equivalentMudWeight')?.value ?? 0),
      pressure: Number(control.get('pressure')?.value ?? 0)
    }));
}

export function normalizePastedPoreFracRows(parsedRows: readonly (readonly string[])[]): readonly (readonly string[])[] {
  if (!parsedRows.length) {
    return parsedRows;
  }

  const uniqueRows = new Map<number, readonly string[]>();

  parsedRows.forEach((row) => {
    const firstValue = Number(row[0]);
    if (!Number.isFinite(firstValue)) {
      return;
    }

    const normalizedKey = Number(firstValue.toFixed(6));
    if (!uniqueRows.has(normalizedKey)) {
      uniqueRows.set(normalizedKey, row);
    }
  });

  return [...uniqueRows.values()].sort((a, b) => Number(a[0]) - Number(b[0]));
}

export interface PoreFracPasteTransformOptions {
  readonly mudlineDepth: number | null | undefined;
  readonly mudlineEquivalentMudWeight: number | null | undefined;
  readonly mudlineTolerance?: number;
}

function toNumericString(value: number): string {
  return Number(value.toFixed(6)).toString();
}

function ensureMudlineAnchorRow(
  rows: readonly (readonly string[])[],
  startRowIndex: number,
  options: PoreFracPasteTransformOptions
): readonly (readonly string[])[] {
  if (startRowIndex > 0 || rows.length === 0) {
    return rows;
  }

  const mudlineDepth = Number(options.mudlineDepth);
  if (!Number.isFinite(mudlineDepth)) {
    return rows;
  }

  const firstDepth = Number(rows[0][0]);
  if (!Number.isFinite(firstDepth)) {
    return rows;
  }

  const tolerance = options.mudlineTolerance ?? 1;
  if (Math.abs(firstDepth - mudlineDepth) < tolerance) {
    const updatedRows = rows.map((row) => [...row]);
    updatedRows[0][0] = toNumericString(mudlineDepth);
    return updatedRows;
  }

  const existingMudlineEmw = Number(options.mudlineEquivalentMudWeight);
  const fallbackEmw = Number(rows[0][1]);
  const mudlineEmw = Number.isFinite(existingMudlineEmw)
    ? existingMudlineEmw
    : Number.isFinite(fallbackEmw) ? fallbackEmw : 0;

  const anchorRow = rows[0].length >= 3
    ? [toNumericString(mudlineDepth), toNumericString(mudlineEmw), '']
    : [toNumericString(mudlineDepth), toNumericString(mudlineEmw)];

  return [anchorRow, ...rows.map((row) => [...row])];
}

export function transformPoreFracPasteRows(
  args: {
    readonly startRowIndex: number;
    readonly parsedRows: readonly (readonly string[])[];
    readonly pasteDialogResult?: Readonly<Record<string, unknown>>;
  },
  options: PoreFracPasteTransformOptions
): readonly (readonly string[])[] {
  const normalizedRows = normalizePastedPoreFracRows(args.parsedRows);

  const transformedRows = args.pasteDialogResult?.['ppFgOption'] === 'tvdPressure'
    ? normalizedRows.map((row) => row.length >= 2 ? [row[0], '-1', row[1]] : row)
    : normalizedRows;

  return ensureMudlineAnchorRow(transformedRows, args.startRowIndex, options);
}

export const poreFracPasteTransform: UnitsGridPasteTransform = ({ parsedRows, pasteDialogResult }) => {
  const normalizedRows = normalizePastedPoreFracRows(parsedRows);

  if (pasteDialogResult?.['ppFgOption'] !== 'tvdPressure') {
    return normalizedRows;
  }

  return normalizedRows.map((row) => row.length >= 2 ? [row[0], '-1', row[1]] : row);
};
